<script setup lang="tsx" generic="T extends BasicForm.BaseData">
import type { Ref, VNode, WatchStopHandle } from 'vue';
import { computed, onMounted, ref, toValue, watch } from 'vue';
import {
  NAutoComplete,
  NCheckbox,
  NDatePicker,
  NInput,
  NInputNumber,
  NRate,
  NSelect,
  NSwitch,
  NTag,
  NTimePicker,
  NTransfer,
  NTree,
  NTreeSelect
} from 'naive-ui';
import _ from 'lodash';
import DictSelect from '@/components/form/dict-select.vue';
import RadioGroup from '@/components/form/radio-group.vue';
import TagCheckbox from '@/components/form/tag-checkbox.vue';
import ProductCascader from '@/components/form/product-cascader.vue';
import BasicUpload from '@/components/form/basic-upload/basic-upload.vue';
import BasicTable from '@/components/advanced/basic-table/basic-table.vue';
import MixedTableForm from '@/components/advanced/mixed-table-form.vue';
import WangEditor from '@/components/advanced/wang-editor.vue';
import type { BasicForm } from '@/typings/basic-form';
import type { CommonType } from '@/typings/common';

export type RegisterParam<FormData> = {
  /** 绑定值 */
  model: Ref<any | any[]>;
  /** 组件 */
  component: any;
  /** 表单数据 */
  formData?: FormData;
  /** 表单源数据 */
  originalData?: FormData;
  /** 操作 */
  disabled: boolean;
  /** 组件实例 */
  componentRef: Ref<any | null>;
  /** 触发器，调用后将立刻触发register方法重新调用 */
  trigger: () => void;
};

export type RegisterTriggerParam<FormData> = {
  /** 绑定值 */
  model: Ref<any | any[]>;
  /** 组件 */
  component: any;
  /** 表单数据 */
  formData: Ref<FormData | undefined>;
  /** 表单源数据 */
  originalData?: FormData;
  /** 操作 */
  disabled: boolean;
  /** 组件实例 */
  componentRef: Ref<any | null>;
  /** 触发器，调用后将立刻触发register方法重新调用 */
  trigger: () => void;
};

/** 动态元素 */
defineOptions({
  name: 'DynamicElement'
  // inheritAttrs: false
});

const model = defineModel<any | any[]>('value', {
  required: true
});

const componentRef = ref(null);

const props = withDefaults(
  defineProps<{
    /** 组件名 */
    // eslint-disable-next-line vue/no-unused-properties
    component?: CommonType.ComponentType;
    /** 组件属性 */
    // eslint-disable-next-line vue/no-unused-properties
    componentProps?: Record<string, any>;
    /** 组件插槽 */
    componentSlots?: Record<string, VNode>;
    /** 表单数据 */
    formData?: T;
    /** 表单源数据 */
    originalData?: T;
    /** 监听表单字段 */
    watchField?: string[];
    /** 使用hooks注册props，使用此方法时componentProps无效 */
    // eslint-disable-next-line vue/no-unused-properties,vue/require-default-prop
    register?: (param: RegisterParam<T>) => Record<string, any>;
    /** 注册触发器，调用触发器将立刻触发register方法重新调用 */
    registerTrigger?: (param: RegisterTriggerParam<T>) => void;
    /** 绑定值字段 */
    vModelField?: string;
    /** 帮助信息 */
    // eslint-disable-next-line vue/no-unused-properties
    helpMessage?: string | null;
    /** 是否禁用 */
    disabled?: boolean;
    bold?: boolean;
    fontSize?: string | null;
    /** disabled时仍然显示组件 */
    showComponentWhenDisabled?: boolean;
    /** 组件前缀 */
    prefix?: ((param: RegisterParam<T>) => VNode | JSX.Element | null) | VNode | JSX.Element | null;
    /** 组件后缀 */
    suffix?: ((param: RegisterParam<T>) => VNode | JSX.Element | null) | VNode | JSX.Element | null;
  }>(),
  {
    component: () => 'NInput',
    componentProps: () => ({}),
    componentSlots: () => ({}),
    vModelField: () => 'value',
    originalData: undefined,
    watchField: undefined,
    registerTrigger: undefined,
    helpMessage: null,
    disabled: false,
    bold: false,
    formData: undefined,
    fontSize: null,
    showComponentWhenDisabled: false,
    prefix: null,
    suffix: null
  }
);

const emit = defineEmits<{
  /** 提交 */
  submit: [];
  change: [];
}>();

const showComponent = computed(() => {
  return !props.disabled || props.showComponentWhenDisabled;
});

// const computedProps = computed(() => {
//   if (showComponent.value) {
//     if (props.register) {
//       console.log('🚀 ~ computedProps ~ ', nodeId);
//       return getRegisterProps(props.register);
//     }
//     return props.componentProps;
//   }
//   return {};
// });

const formData = computed(() => props.formData);

const computedProps = ref({});

function resetComputedProps() {
  if (props.register) {
    computedProps.value = props.register({
      model,
      component: props.component,
      disabled: props.disabled,
      originalData: props.originalData,
      formData: formData.value,
      componentRef,
      trigger: resetComputedProps
    });
  }
}
watch(
  showComponent,
  () => {
    if (showComponent.value) {
      if (props.register) {
        resetComputedProps();
      } else {
        computedProps.value = props.componentProps;
      }
    }
  },
  {
    immediate: true
  }
);

const prefixComponent = ref<VNode | JSX.Element | null>(null);
const suffixComponent = ref<VNode | JSX.Element | null>(null);

function resetPrefix() {
  if (props.prefix) {
    if (typeof props.prefix === 'function') {
      prefixComponent.value = props.prefix({
        model,
        component: props.component,
        disabled: props.disabled,
        originalData: props.originalData,
        formData: formData.value,
        componentRef,
        trigger: resetComputedProps
      });
    } else {
      prefixComponent.value = props.prefix as VNode;
    }
  } else {
    prefixComponent.value = null;
  }
}

function resetSuffix() {
  if (props.suffix) {
    if (typeof props.suffix === 'function') {
      suffixComponent.value = props.suffix({
        model,
        component: props.component,
        disabled: props.disabled,
        originalData: props.originalData,
        formData: formData.value,
        componentRef,
        trigger: resetComputedProps
      });
    } else {
      suffixComponent.value = props.suffix as VNode;
    }
  } else {
    suffixComponent.value = null;
  }
}

onMounted(() => {
  if (props.registerTrigger) {
    props.registerTrigger({
      model,
      component: props.component,
      disabled: props.disabled,
      originalData: props.originalData,
      formData,
      componentRef,
      trigger: resetComputedProps
    });
  }
  if (props.register) {
    watch(
      () => [props.register, props.disabled, props.formData],
      () => {
        if (props.register) {
          resetComputedProps();
        }
      }
    );
    let unwatchWatchField: WatchStopHandle | null = null;
    watch(
      () => props.watchField,
      () => {
        if (unwatchWatchField !== null) {
          unwatchWatchField();
          unwatchWatchField = null;
        }
        if (props.watchField && props.watchField.length > 0) {
          unwatchWatchField = watch(
            () => {
              return props.watchField?.map(item => _.get(formData.value, item));
            },
            () => {
              if (props.register) {
                resetComputedProps();
              }
            }
          );
        }
      },
      {
        immediate: true,
        deep: true
      }
    );
  } else {
    watch(
      () => props.componentProps,
      () => {
        computedProps.value = props.componentProps;
      }
    );
  }
  if (props.prefix) {
    resetPrefix();
    watch(
      () => [props.prefix, props.disabled, props.formData],
      () => {
        if (props.prefix) {
          resetPrefix();
        }
      }
    );
  }
  if (props.suffix) {
    resetSuffix();
    watch(
      () => [props.suffix, props.disabled, props.formData],
      () => {
        if (props.suffix) {
          resetSuffix();
        }
      }
    );
  }
});

const components: Record<CommonType.ComponentType, any> = {
  NInput,
  NInputNumber,
  NSelect,
  NTreeSelect,
  NTag,
  NDatePicker,
  NTimePicker,
  NCheckbox,
  NSwitch,
  NAutoComplete,
  NTree,
  NRate,
  NTransfer,
  DictSelect,
  RadioGroup,
  TagCheckbox,
  BasicUpload,
  BasicTable,
  MixedTableForm,
  WangEditor,
  ProductCascader
};
</script>

<template>
  <div
    v-if="!showComponent"
    class="min-h-30px w-full rounded-md bg-gray-100 px-12px py-3px text-black dark:bg-op-10 dark:text-white"
    :style="{
      fontWeight: bold ? 'bold' : 'normal',
      fontSize: fontSize ? fontSize : ''
    }"
  >
    {{ model }}
  </div>
  <template v-else>
    <div class="w-full flex">
      <component :is="prefixComponent" v-if="prefixComponent" />
      <component
        :is="components[component]"
        ref="componentRef"
        v-model:[toValue(vModelField)]="model"
        v-bind="computedProps"
        :disabled="disabled"
        @submit="emit('submit')"
        @keyup.enter="emit('submit')"
        @input="emit('change')"
        @update="emit('change')"
        @change="emit('change')"
      >
        <template v-for="(_slot, name) in $slots" #[name]="data" :key="name">
          <slot :name="name" v-bind="data"></slot>
        </template>
        <template v-for="(value, key) in componentSlots" :key="key" #[key]>
          <component :is="value" :key="key" />
        </template>
      </component>
      <component :is="suffixComponent" v-if="suffixComponent" />
    </div>
  </template>
</template>

<style scoped></style>
